/**
 * ChatHistory — session list that replaces the messages area.
 * Header is managed by ChatPanel (shows "Chat History" + back arrow).
 */

import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  gatewayClient,
  SessionItem,
  SessionMessage,
} from '../../client/GatewayClient';
import { useImpersonation } from '../../context/ImpersonationContext';
import './ChatHistory.css';

interface ChatHistoryProps {
  onLoadSession: (sessionId: string, messages: SessionMessage[], nextToken?: string) => void;
  /** Active agent (from ChatPanel's agentName prop) used to scope the session list. */
  agentName: string;
}

type HistoryErrorKey = 'loadFailed' | 'loadMoreFailed' | 'loadConversationFailed';

export default function ChatHistory({ onLoadSession, agentName }: ChatHistoryProps) {
  const { effectiveUid, isImpersonating } = useImpersonation();
  const [sessions, setSessions] = useState<SessionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation('chat');
  // A server message is shown as-is; otherwise a key, translated at render so it
  // follows the locale without re-running the fetch effects.
  const [error, setError] = useState<{ message: string } | { key: HistoryErrorKey } | null>(null);
  const [loadingSessionId, setLoadingSessionId] = useState<string | null>(null);

  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const nextTokenRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await gatewayClient.listSessions({ source: 'rocky', limit: 50, agent_id: agentName, ...(isImpersonating ? { user: effectiveUid } : {}) });
        if (!cancelled) {
          setSessions(resp.sessions);
          nextTokenRef.current = resp.next_token;
          setHasMore(!!resp.next_token);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? { message: err.message } : { key: 'loadFailed' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [effectiveUid, agentName]);

  const loadingMoreRef = useRef(false);

  const loadMore = useCallback(async () => {
    if (!nextTokenRef.current || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const resp = await gatewayClient.listSessions({ source: 'rocky', limit: 50, next_token: nextTokenRef.current, agent_id: agentName, ...(isImpersonating ? { user: effectiveUid } : {}) });
      setSessions(prev => [...prev, ...resp.sessions]);
      nextTokenRef.current = resp.next_token;
      setHasMore(!!resp.next_token);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? { message: err.message } : { key: 'loadMoreFailed' });
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [isImpersonating, effectiveUid]);

  const handleSessionClick = useCallback(async (session: SessionItem) => {
    setLoadingSessionId(session.session_id);
    try {
      const resp = await gatewayClient.listSessionMessages(session.session_id, {
        limit: 100,
        order: 'desc',
        // Gateway verifies session ownership against the effective user, so an
        // admin browsing another user's sessions must pass ?user= here too or
        // the request 404s (DAT-9051).
        ...(isImpersonating ? { user: effectiveUid } : {}),
      });
      onLoadSession(session.session_id, resp.messages.reverse(), resp.next_token);
    } catch (err) {
      console.error('Failed to load session messages:', err);
      setError({ key: 'loadConversationFailed' });
      setLoadingSessionId(null);
    }
  }, [onLoadSession, isImpersonating, effectiveUid]);

  const formatTime = (isoStr: string): string => {
    try {
      const date = new Date(isoStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return t('history.justNow');
      if (diffMin < 60) return t('history.minutesAgo', { n: diffMin });
      const diffHr = Math.floor(diffMin / 60);
      if (diffHr < 24) return t('history.hoursAgo', { n: diffHr });
      const diffDay = Math.floor(diffHr / 24);
      if (diffDay < 7) return t('history.daysAgo', { n: diffDay });
      return date.toLocaleDateString();
    } catch {
      return '';
    }
  };

  return (
    <div className="chat-history__list">
      {loading && (
        <div className="chat-history__loading">{t('history.loading')}</div>
      )}

      {error && (
        <div className="chat-history__error">{'message' in error ? error.message : t(`history.${error.key}`)}</div>
      )}

      {!loading && !error && sessions.length === 0 && (
        <div className="chat-history__empty">{t('history.empty')}</div>
      )}

      {sessions.map(session => (
        <button
          key={session.session_id}
          className={`chat-history__item ${
            loadingSessionId === session.session_id ? 'chat-history__item--loading' : ''
          }`}
          onClick={() => handleSessionClick(session)}
          disabled={loadingSessionId !== null}
        >
          <div className="chat-history__item-title">
            {session.description || t('history.untitled')}
          </div>
          <div className="chat-history__item-meta">
            <span className="chat-history__item-time">
              {formatTime(session.update_time)}
            </span>
            {session.agent_id && (
              <span className="chat-history__item-agent">
                {session.agent_id}
              </span>
            )}
          </div>
        </button>
      ))}

      {hasMore && (
        <button
          className="chat-history__load-more"
          onClick={loadMore}
          disabled={loadingMore}
        >
          {loadingMore ? t('history.loadingMore') : t('history.loadMore')}
        </button>
      )}
    </div>
  );
}
